import { rect, type Rect, type Vec2 } from './layout';

/**
 * Interiors are real rooms you walk around in. They're built out past the east edge
 * of the map, one slot per room, far enough apart that you can't see the next one.
 * Plain data here (no three.js) so tests and the network code can share it.
 */

export type RoomKind = 'single' | 'double' | 'bar' | 'gas' | 'pawn' | 'booze' | 'laundry' | 'ufo';

/** First room's center x, and the distance between rooms. */
export const ROOM_X0 = 400;
export const ROOM_SPACING = 50;
/** Slots 0..8 are the home lots (same index as HOME_LOTS); the bar and the shops have their own. */
export const BAR_SLOT = 12;
export const GAS_SLOT = 13;
export const PAWN_SLOT = 14;
export const BOOZE_SLOT = 15;
export const LAUNDRY_SLOT = 16;
/** The neighbors on the north end of Main Street, who'll have you in. */
export const BRENDA_SLOT = 17;
export const WANDA_SLOT = 18;
export const HANK_SLOT = 19;
/** Somewhere above the park. Don't ask. */
export const UFO_SLOT = 20;
/** Darlene's park office and Cooter's sales trailer. */
export const OFFICE_SLOT = 21;
export const COOTER_SLOT = 22;
/** The rest of the neighbors' trailers on Main Street, and Meemaw's on Possum Trot. */
export const TAMMY_SLOT = 23;
export const EARL_SLOT = 24;
export const DALE_SLOT = 25;
export const JENKINS_SLOT = 26;
export const SKEETER_SLOT = 27;
export const MEEMAW_SLOT = 28;
const LAST_SLOT = MEEMAW_SLOT;
/** Anything east of this is inside a room. */
export const INTERIOR_MIN_X = ROOM_X0 - ROOM_SPACING / 2;
export const INTERIOR_MAX_X = ROOM_X0 + (LAST_SLOT + 1) * ROOM_SPACING;

export const roomCenter = (slot: number): Vec2 => ({ x: ROOM_X0 + slot * ROOM_SPACING, z: 0 });

/** Which room slot a point is in, or -1 outdoors. */
export function roomSlotAt(x: number): number {
  if (x < INTERIOR_MIN_X) return -1;
  return Math.round((x - ROOM_X0) / ROOM_SPACING);
}

export interface RoomShape {
  /** Inside dimensions: x, z and wall height. */
  length: number;
  depth: number;
  height: number;
  /** Local x of the front door (on the +z wall). */
  doorX: number;
}

export const ROOM_SHAPE: Record<RoomKind, RoomShape> = {
  single: { length: 11.6, depth: 3.6, height: 2.4, doorX: 4 },
  double: { length: 15.6, depth: 6.8, height: 2.5, doorX: 4 },
  bar: { length: 18, depth: 12, height: 3.2, doorX: 6 },
  gas: { length: 14, depth: 9, height: 3.3, doorX: 0 },
  pawn: { length: 12, depth: 9, height: 3.3, doorX: 0 },
  booze: { length: 13, depth: 10, height: 3.8, doorX: 0 },
  laundry: { length: 14, depth: 9, height: 3.1, doorX: 0.8 },
  ufo: { length: 12, depth: 12, height: 4, doorX: 0 },
};

/** Inside bounds of a room in world space. */
export function roomBounds(slot: number, kind: RoomKind): Rect {
  const c = roomCenter(slot);
  const s = ROOM_SHAPE[kind];
  return rect(c.x - s.length / 2, c.z - s.depth / 2, c.x + s.length / 2, c.z + s.depth / 2);
}

export type FurnitureKind =
  | 'bed'
  | 'dresser'
  | 'couch'
  | 'couch2'
  | 'coffeeTable'
  | 'tv'
  | 'lamp'
  | 'rug'
  | 'sinkCounter'
  | 'counter'
  | 'stove'
  | 'fridge'
  | 'dinette'
  | 'toilet'
  | 'tub'
  | 'bathSink'
  | 'mat';

export interface Placement {
  x: number;
  z: number;
  /** Rotation about y; the piece's front is +z at 0. */
  rot?: number;
  /** Size for rugs and counters. */
  w?: number;
  d?: number;
  /** Hidden while this interior item is out (a waterbed replaces the bed). */
  unless?: string;
}

export interface Partition {
  /** A half-height wall from a to b. */
  a: Vec2;
  b: Vec2;
}

export interface DecorPlace {
  x: number;
  z: number;
  /** Height of the center for wall items. */
  y?: number;
  rot: number;
}

export interface RoomLayout {
  furniture: Array<[FurnitureKind, Placement]>;
  partitions: Partition[];
  /** Window centers: [x, wall side (+1 front, -1 back)] or end walls via 'x'. */
  windows: Array<{ x: number; side: 1 | -1 }>;
  /** Where each interior item goes. Items without a place here don't fit. */
  decor: Record<string, DecorPlace>;
  /** Interaction spots, by name, in local coordinates. */
  spots: Record<'bed' | 'couch' | 'tv' | 'fridge' | 'toilet', Vec2>;
}

const HALF_PI = Math.PI / 2;

/** A single-wide: bedroom west, tiny bathroom, kitchen, living room by the door. */
const SINGLE: RoomLayout = {
  furniture: [
    ['mat', { x: 4, z: 1.45 }],
    ['rug', { x: 2.2, z: -0.25, w: 2.4, d: 1.7 }],
    ['couch', { x: 2.2, z: -1.3 }],
    ['coffeeTable', { x: 2.2, z: -0.1 }],
    ['tv', { x: 2.2, z: 1.5, rot: Math.PI }],
    ['lamp', { x: 3.6, z: -1.5 }],
    ['sinkCounter', { x: -0.6, z: -1.5, w: 1.6 }],
    ['stove', { x: 0.68, z: -1.48 }],
    ['fridge', { x: -1.85, z: -1.45 }],
    ['dinette', { x: -0.5, z: 0.9 }],
    ['toilet', { x: -2.85, z: -1.45 }],
    ['bed', { x: -4.7, z: -0.55, rot: HALF_PI, unless: 'waterbed' }],
    ['dresser', { x: -4.55, z: 1.5, rot: Math.PI }],
  ],
  partitions: [
    { a: { x: -2.3, z: -1.8 }, b: { x: -2.3, z: -0.3 } },
    { a: { x: -3.4, z: -1.8 }, b: { x: -3.4, z: -0.3 } },
    { a: { x: -3.4, z: 0.85 }, b: { x: -3.4, z: 1.8 } },
  ],
  windows: [
    { x: 0.95, side: 1 },
    { x: -4.95, side: 1 },
    { x: -0.6, side: -1 },
    { x: 4.9, side: -1 },
  ],
  decor: {
    dogsPoker: { x: 2.2, z: -1.8, y: 1.6, rot: 0 },
    wolfTapestry: { x: -4.4, z: -1.8, y: 1.62, rot: 0 },
    deerMount: { x: -5.8, z: -0.55, y: 1.85, rot: HALF_PI },
    singingBass: { x: -0.5, z: 1.8, y: 1.62, rot: Math.PI },
    neonBeer: { x: 5.2, z: 1.8, y: 1.72, rot: Math.PI },
    dreamCatcher: { x: -3.9, z: 1.8, y: 1.75, rot: Math.PI },
    lazyBoy: { x: 4.95, z: -0.5, rot: -0.86 },
    waterbed: { x: -4.65, z: -0.55, rot: HALF_PI },
    beerFridge: { x: -1.7, z: 1.45, rot: Math.PI },
    lavaLamp: { x: -3.0, z: 1.55, rot: Math.PI },
    fishTank: { x: 5.35, z: 0.95, rot: -HALF_PI },
    karaoke: { x: 1.1, z: 1.45, rot: Math.PI },
    canPyramid: { x: 4.75, z: -1.58, rot: 0 },
  },
  spots: {
    bed: { x: -4.2, z: 0.6 },
    couch: { x: 2.2, z: -0.75 },
    tv: { x: 2.2, z: 0.85 },
    fridge: { x: -1.85, z: -0.7 },
    toilet: { x: -2.85, z: -0.7 },
  },
};

/** The double wide: living room and kitchen east, bedroom, den and a real bathroom west. */
const DOUBLE: RoomLayout = {
  furniture: [
    ['mat', { x: 4, z: 3.05 }],
    ['rug', { x: 1.0, z: 0.7, w: 3.4, d: 2.6 }],
    ['couch', { x: 1.0, z: -0.65 }],
    ['couch2', { x: -1.05, z: 0.8, rot: HALF_PI }],
    ['coffeeTable', { x: 1.0, z: 0.75 }],
    ['tv', { x: 1.0, z: 3.1, rot: Math.PI }],
    ['lamp', { x: 2.5, z: -1.1 }],
    ['sinkCounter', { x: 7.45, z: -1.0, rot: -HALF_PI, w: 2.0 }],
    ['stove', { x: 7.45, z: 0.75, rot: -HALF_PI }],
    ['fridge', { x: 7.45, z: -2.95, rot: -HALF_PI }],
    ['counter', { x: 5.4, z: -3.1, w: 2.6 }],
    ['dinette', { x: 5.2, z: 1.6 }],
    ['bed', { x: -5.4, z: -2.25, unless: 'waterbed' }],
    ['dresser', { x: -2.9, z: -3.1 }],
    ['tub', { x: -6.85, z: 2.95, rot: Math.PI }],
    ['toilet', { x: -5.95, z: 0.6 }],
    ['bathSink', { x: -7.5, z: 1.3, rot: HALF_PI }],
  ],
  partitions: [
    { a: { x: -7.8, z: 0 }, b: { x: -2.0, z: 0 } },
    { a: { x: -2.0, z: -3.4 }, b: { x: -2.0, z: -2.4 } },
    { a: { x: -2.0, z: -1.0 }, b: { x: -2.0, z: 1.0 } },
    { a: { x: -2.0, z: 2.4 }, b: { x: -2.0, z: 3.4 } },
    { a: { x: -5.3, z: 0 }, b: { x: -5.3, z: 1.6 } },
    { a: { x: -5.3, z: 2.6 }, b: { x: -5.3, z: 3.4 } },
  ],
  windows: [
    { x: 2.6, side: 1 },
    { x: 6.4, side: 1 },
    { x: -3.7, side: 1 },
    { x: -1.0, side: -1 },
    { x: 3.4, side: -1 },
    { x: -6.6, side: -1 },
  ],
  decor: {
    dogsPoker: { x: 1.0, z: -3.4, y: 1.65, rot: 0 },
    wolfTapestry: { x: -5.4, z: -3.4, y: 1.8, rot: 0 },
    deerMount: { x: -0.7, z: 3.4, y: 1.85, rot: Math.PI },
    singingBass: { x: 5.4, z: -3.4, y: 1.62, rot: 0 },
    neonBeer: { x: 5.6, z: 3.4, y: 1.75, rot: Math.PI },
    dreamCatcher: { x: -3.0, z: -3.4, y: 1.75, rot: 0 },
    lazyBoy: { x: 3.0, z: 0.5, rot: -0.75 },
    waterbed: { x: -5.4, z: -2.2, rot: 0 },
    beerFridge: { x: -1.4, z: -3.0, rot: 0 },
    lavaLamp: { x: -1.5, z: 2.95, rot: Math.PI },
    fishTank: { x: 3.7, z: -3.15, rot: 0 },
    karaoke: { x: -4.85, z: 0.55, rot: 0 },
    tanningBed: { x: -3.75, z: 2.85, rot: Math.PI },
    gameConsole: { x: -3.4, z: 0.55, rot: 0 },
    canPyramid: { x: 6.4, z: 3.1, rot: Math.PI },
  },
  spots: {
    bed: { x: -4.0, z: -1.2 },
    couch: { x: 1.0, z: -0.1 },
    tv: { x: 1.0, z: 2.4 },
    fridge: { x: 6.6, z: -2.95 },
    toilet: { x: -5.95, z: 1.3 },
  },
};

export const ROOM_LAYOUT: Record<'single' | 'double', RoomLayout> = { single: SINGLE, double: DOUBLE };

/** True if an interior item has a place in this kind of home. */
export const fitsIn = (kind: 'single' | 'double', id: string): boolean => id in ROOM_LAYOUT[kind].decor;

/** A point inside a room (local coordinates) in the world. */
export function roomSpot(slot: number, p: Vec2): Vec2 {
  const c = roomCenter(slot);
  return { x: c.x + p.x, z: c.z + p.z };
}
